// Variables des contrats SwiftEats (livreur + commerçant) : la PARTIE adverse est désignée selon son
// STATUT JURIDIQUE (migration 0050), à partir des données de la base, et le modèle de contrat la reçoit
// par substitution de `{{marques}}`.
//
//   {{designation_partie}}  paragraphe qui désigne la partie (société / entreprenant / personne physique)
//   {{nom_partie}}          nom pour les blocs de signature
//   {{date_du_jour}}        « 7 octobre 2026 »
//
// MODULE PUR — AUCUN réseau, AUCUNE base, AUCUN import : testé par contrat-variables.test.ts (Deno), importé
// tel quel par les consoles web (dispatch : aperçu en direct de la désignation) et par les edge functions
// `envoyer-contrat-signature` (PDF Documenso) et `contrat-rendu` (texte affiché puis accepté dans l'app).
// C'est l'UNIQUE source de rendu : jamais de seconde implémentation en Kotlin, Swift ou SQL — les apps
// reçoivent le texte déjà rendu (`contrat-rendu`).
//
// Règle héritée de SwiftDeliver : une marque inconnue, ou une donnée manquante, BLOQUE le rendu. Jamais un
// contrat parti avec un trou.
//
// ⚠️ À VALIDER PAR LE JURISTE : les trois formules de `designationPartie` ci-dessous (ordre des mentions,
// « Nom Prénoms » avec le nom en capitales, « de la carte NINA », libellé « personne morale » pour la forme
// « autre », mention OHADA des entreprenants). Elles sont l'unique endroit où ce texte vit.

export type StatutJuridique = "societe" | "entreprenant" | "personne_physique";
export type SujetContrat = "commercant" | "livreur";
export type FormeJuridique = "SARL" | "SA" | "SAS" | "SNC" | "GIE" | "SCS" | "autre";
export type TypePiece = "cni" | "passeport" | "carte_nina" | "carte_consulaire" | "autre";

/** Données d'identité telles que les renvoie `identite_pour_contrat()` (SQL). Tout est optionnel : on valide ici. */
export interface IdentiteContrat {
  statut_juridique?: StatutJuridique | string | null;
  forme_juridique?: FormeJuridique | string | null;
  raison_sociale?: string | null;
  nom_commercial?: string | null;
  capital_fcfa?: number | string | null;
  rccm?: string | null;
  nif?: string | null;
  numero_declaration_entreprenant?: string | null;
  siege_adresse?: string | null;
  representant_nom?: string | null;
  representant_qualite?: string | null;
  personne_nom?: string | null;
  personne_prenoms?: string | null;
  /** « AAAA-MM-JJ » */
  date_naissance?: string | null;
  lieu_naissance?: string | null;
  type_piece?: TypePiece | string | null;
  numero_piece?: string | null;
  adresse_personne?: string | null;
}

export const STATUTS_JURIDIQUES: readonly StatutJuridique[] = ["societe", "entreprenant", "personne_physique"];
export const FORMES_JURIDIQUES: readonly FormeJuridique[] = ["SARL", "SA", "SAS", "SNC", "GIE", "SCS", "autre"];
export const TYPES_PIECE: readonly TypePiece[] = ["cni", "passeport", "carte_nina", "carte_consulaire", "autre"];

export const LIBELLE_STATUT: Record<StatutJuridique, string> = {
  societe: "Société",
  entreprenant: "Entreprenant (entreprise individuelle déclarée)",
  personne_physique: "Personne physique (non immatriculée)",
};

export const LIBELLE_FORME: Record<FormeJuridique, string> = {
  SARL: "société à responsabilité limitée (SARL)",
  SA: "société anonyme (SA)",
  SAS: "société par actions simplifiée (SAS)",
  SNC: "société en nom collectif (SNC)",
  GIE: "groupement d'intérêt économique (GIE)",
  SCS: "société en commandite simple (SCS)",
  // Forme libre non précisée : on dit seulement ce qui est sûr.
  autre: "personne morale",
};

/** Libellé précédé de son article : « de la carte NINA », « du passeport ». */
export const LIBELLE_PIECE: Record<TypePiece, string> = {
  cni: "de la carte nationale d'identité",
  passeport: "du passeport",
  carte_nina: "de la carte NINA",
  carte_consulaire: "de la carte consulaire",
  autre: "de la pièce d'identité",
};

/** Libellés des champs, pour les listes « il manque… » des consoles et des apps. */
export const LIBELLE_CHAMP: Record<string, string> = {
  statut_juridique: "Statut juridique",
  forme_juridique: "Forme juridique",
  raison_sociale: "Raison sociale",
  nom_commercial: "Nom commercial",
  capital_fcfa: "Capital (FCFA)",
  rccm: "Numéro RCCM",
  nif: "NIF",
  numero_declaration_entreprenant: "Numéro de déclaration d'entreprenant",
  siege_adresse: "Adresse du siège",
  representant_nom: "Nom du représentant",
  representant_qualite: "Qualité du représentant",
  personne_nom: "Nom",
  personne_prenoms: "Prénoms",
  date_naissance: "Date de naissance",
  lieu_naissance: "Lieu de naissance",
  type_piece: "Type de pièce d'identité",
  numero_piece: "Numéro de la pièce d'identité",
  adresse_personne: "Adresse",
};

/**
 * Champs OBLIGATOIRES par sujet et par statut.
 * ⚠️ MÊME LISTE que la fonction SQL `identite_champs_requis()` (migration 0050) : les deux tests
 * (tests/onboarding/40_scenarios_0050.sql et contrat-variables.test.ts) portent le même tableau.
 * La base reste l'autorité qui BLOQUE ; ce tableau sert à l'aperçu et au rendu.
 */
export const CHAMPS_REQUIS: Record<SujetContrat, Record<StatutJuridique, readonly string[]>> = {
  commercant: {
    societe: ["raison_sociale", "forme_juridique", "rccm", "nif", "siege_adresse", "representant_nom", "representant_qualite"],
    entreprenant: ["personne_nom", "personne_prenoms", "numero_declaration_entreprenant", "adresse_personne"],
    personne_physique: ["personne_nom", "personne_prenoms", "type_piece", "numero_piece", "adresse_personne"],
  },
  livreur: {
    // Un livreur n'est jamais une société : le statut « societe » n'a aucun champ requis (il est refusé en amont).
    societe: [],
    entreprenant: [
      "personne_nom", "personne_prenoms", "date_naissance", "lieu_naissance", "type_piece", "numero_piece",
      "adresse_personne", "numero_declaration_entreprenant",
    ],
    personne_physique: [
      "personne_nom", "personne_prenoms", "date_naissance", "lieu_naissance", "type_piece", "numero_piece",
      "adresse_personne",
    ],
  },
};

export const VARIABLES_CONNUES = ["designation_partie", "nom_partie", "date_du_jour"] as const;

export class IdentiteIncomplete extends Error {
  constructor(public manquants: string[]) {
    super(`identite_incomplete: ${manquants.join(", ")}`);
  }
}
export class VariablesInconnues extends Error {
  constructor(public inconnues: string[]) {
    super(`variables_inconnues: ${inconnues.join(", ")}`);
  }
}

// ── Mise en forme française ───────────────────────────────────────────────

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

/** 1000000 → « 1 000 000 » (espaces simples : le PDF n'embarque que Helvetica/WinAnsi). */
export function nombreFr(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
export const fcfa = (n: number) => `${nombreFr(n)} FCFA`;

/** Une date en toutes lettres (jour UTC : Bamako est à l'heure UTC) — « 1er mars 2026 », « 12 mars 1990 ». */
export function dateFr(d: Date): string {
  const j = d.getUTCDate();
  return `${j === 1 ? "1er" : j} ${MOIS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** « AAAA-MM-JJ » (ou ISO complet) → « 12 mars 1990 » ; `null` si la date est illisible ou impossible. */
export function dateIsoFr(iso: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso ?? "").trim());
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(t);
  // Refuse 2026-02-31 que Date.UTC « corrige » en silence.
  if (d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return null;
  return dateFr(d);
}

const net = (v: unknown): string => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "").trim();

/** « KEITA Mamadou » : le nom en capitales, les prénoms tels que saisis. */
export function nomComplet(nom: string, prenoms: string): string {
  return `${net(nom).toLocaleUpperCase("fr-FR")} ${net(prenoms)}`.trim();
}

function capital(v: IdentiteContrat["capital_fcfa"]): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/\s/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

// ── Règles ────────────────────────────────────────────────────────────────

export function statutDe(identite: IdentiteContrat | null | undefined): StatutJuridique | null {
  const s = net(identite?.statut_juridique);
  return (STATUTS_JURIDIQUES as readonly string[]).includes(s) ? (s as StatutJuridique) : null;
}

/** Champs requis encore vides. `["statut_juridique"]` tant que le statut n'est pas choisi. */
export function champsManquants(sujet: SujetContrat, identite: IdentiteContrat | null | undefined): string[] {
  const statut = statutDe(identite);
  if (statut === null) return ["statut_juridique"];
  const i = identite as unknown as Record<string, unknown>;
  const manquants = CHAMPS_REQUIS[sujet][statut].filter((c) => net(i[c]) === "");
  // Un livreur ne peut pas être une société.
  if (sujet === "livreur" && statut === "societe") return ["statut_juridique"];
  return [...manquants];
}

// ── Désignation de la partie ──────────────────────────────────────────────

/**
 * Le paragraphe qui désigne la partie, terminé par une virgule (le modèle enchaîne « ci-après le … »).
 * Lève `IdentiteIncomplete` s'il manque un champ requis.
 */
export function designationPartie(sujet: SujetContrat, identite: IdentiteContrat): string {
  const manquants = champsManquants(sujet, identite);
  if (manquants.length) throw new IdentiteIncomplete(manquants);
  const statut = statutDe(identite)!;
  const v = identite as unknown as Record<string, unknown>;
  const g = (k: string) => net(v[k]);

  if (statut === "societe") {
    const forme = LIBELLE_FORME[g("forme_juridique") as FormeJuridique] ?? "personne morale";
    const cap = capital(identite.capital_fcfa);
    return `La société ${g("raison_sociale")}, ${forme}${cap !== null ? ` au capital de ${fcfa(cap)}` : ""}, ` +
      `immatriculée au Registre du Commerce et du Crédit Mobilier de Bamako sous le numéro ${g("rccm")}, ` +
      `NIF ${g("nif")}, dont le siège est situé ${g("siege_adresse")}, ` +
      `représentée par ${g("representant_nom")}, en sa qualité de ${g("representant_qualite")},`;
  }

  const nom = nomComplet(g("personne_nom"), g("personne_prenoms"));

  if (statut === "entreprenant") {
    const commercial = g("nom_commercial");
    const nif = g("nif");
    return `${nom}, entreprenant au sens de l'Acte uniforme OHADA relatif au droit commercial général, ` +
      `déclaré sous le numéro ${g("numero_declaration_entreprenant")}` +
      `${commercial ? `, exerçant sous le nom commercial ${commercial}` : ""}` +
      `${nif ? `, NIF ${nif}` : ""}, domicilié(e) ${g("adresse_personne")},`;
  }

  // personne_physique
  const date = dateIsoFr(g("date_naissance"));
  const lieu = g("lieu_naissance");
  const naissance = date && lieu ? `, né(e) le ${date} à ${lieu}`
    : date ? `, né(e) le ${date}`
    : lieu ? `, né(e) à ${lieu}`
    : "";
  const piece = LIBELLE_PIECE[g("type_piece") as TypePiece] ?? LIBELLE_PIECE.autre;
  return `${nom}${naissance}, titulaire ${piece} n° ${g("numero_piece")}, demeurant ${g("adresse_personne")},`;
}

/** Le nom qui figure dans les blocs de signature. */
export function nomPartie(sujet: SujetContrat, identite: IdentiteContrat): string {
  const manquants = champsManquants(sujet, identite);
  if (manquants.length) throw new IdentiteIncomplete(manquants);
  const g = (k: string) => net((identite as unknown as Record<string, unknown>)[k]);
  if (statutDe(identite) === "societe") {
    return `${g("raison_sociale")}, représentée par ${g("representant_nom")}, ${g("representant_qualite")}`;
  }
  return nomComplet(g("personne_nom"), g("personne_prenoms"));
}

export function construireVariables(
  sujet: SujetContrat,
  identite: IdentiteContrat,
  maintenant: Date,
): Record<string, string> {
  return {
    designation_partie: designationPartie(sujet, identite),
    nom_partie: nomPartie(sujet, identite),
    date_du_jour: dateFr(maintenant),
  };
}

// ── Substitution ──────────────────────────────────────────────────────────

const MARQUE = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** Les marques `{{x}}` d'un modèle, distinguées en connues et inconnues. */
export function marquesDuModele(md: string): { connues: string[]; inconnues: string[] } {
  const vues = new Set<string>();
  for (const m of md.matchAll(MARQUE)) vues.add(m[1]!);
  const connues: string[] = [];
  const inconnues: string[] = [];
  for (const n of vues) ((VARIABLES_CONNUES as readonly string[]).includes(n) ? connues : inconnues).push(n);
  return { connues, inconnues };
}

/** Accolades doubles mal formées (« {{ nom », « {nom}} »…) : elles ne seraient JAMAIS remplacées. */
export function accoladesSuspectes(md: string): string[] {
  const sansMarques = md.replace(MARQUE, "");
  return [...sansMarques.matchAll(/\{\{|\}\}/g)].map((m) => m[0]);
}

/**
 * Passages « [ … ] » restés dans un texte : des trous à compléter avant publication. Les liens markdown
 * `[texte](url)` ne comptent pas.
 */
export function crochetsRestants(md: string): string[] {
  const out: string[] = [];
  for (const m of md.matchAll(/\[([^\]\n]*)\](?!\()/g)) out.push(`[${m[1]}]`);
  return out;
}

/** Remplace chaque `{{variable}}` ; une marque inconnue du dictionnaire fait échouer (jamais de trou). */
export function rendreModele(md: string, variables: Record<string, string>): string {
  const inconnues = new Set<string>();
  const rendu = md.replace(MARQUE, (brut, nom: string) => {
    if (Object.prototype.hasOwnProperty.call(variables, nom)) return variables[nom]!;
    inconnues.add(nom);
    return brut;
  });
  if (inconnues.size) throw new VariablesInconnues([...inconnues]);
  return rendu;
}

/** SHA-256 hexadécimal du texte encodé en UTF-8 — identique à `encode(sha256(convert_to(t,'UTF8')),'hex')` en SQL. */
export async function sha256Hex(texte: string): Promise<string> {
  const octets = new TextEncoder().encode(texte);
  const h = await crypto.subtle.digest("SHA-256", octets);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export interface ContratRendu { texte: string; sha256: string }

/** Rend un modèle pour une partie et calcule l'empreinte du texte RENDU. */
export async function rendreContrat(
  corpsMd: string,
  sujet: SujetContrat,
  identite: IdentiteContrat,
  maintenant: Date,
): Promise<ContratRendu> {
  const texte = rendreModele(corpsMd, construireVariables(sujet, identite, maintenant));
  return { texte, sha256: await sha256Hex(texte) };
}

/**
 * Le texte à proposer à la signature : un modèle SANS marque (versions publiées avant la 0050) est servi tel
 * quel, sans exiger d'identité — la base l'exige déjà avant d'accepter ; sinon rendu complet.
 */
export async function textePourSignature(
  corpsMd: string,
  sujet: SujetContrat,
  identite: IdentiteContrat | null,
  maintenant: Date,
): Promise<ContratRendu> {
  const { connues, inconnues } = marquesDuModele(corpsMd);
  if (connues.length === 0 && inconnues.length === 0) return { texte: corpsMd, sha256: await sha256Hex(corpsMd) };
  return rendreContrat(corpsMd, sujet, identite ?? {}, maintenant);
}
